import type { Board } from '@atlassian/jira-navigation-types/src/types.tsx';
import type { BoardsPage, OnBoardClick, AccountId, OnNavigateUrl } from '../model/index.tsx';

export const FETCH_INITIAL_BOARDS = 'state.FETCH_INITIAL_BOARDS' as const;
export type FetchInitialBoardsAction = {
	type: typeof FETCH_INITIAL_BOARDS;
	payload: boolean;
};
export const fetchInitialBoards = (forceRetrieve = false): FetchInitialBoardsAction => ({
	type: FETCH_INITIAL_BOARDS,
	payload: forceRetrieve,
});

export const START_FETCHING_INITIAL_BOARDS = 'state.START_FETCHING_INITIAL_BOARDS' as const;
export type StartFetchingInitialBoardsAction = {
	type: typeof START_FETCHING_INITIAL_BOARDS;
};
export const startFetchingInitialBoards = (): StartFetchingInitialBoardsAction => ({
	type: START_FETCHING_INITIAL_BOARDS,
});

export const INITIAL_BOARDS_FETCHED = 'state.INITIAL_BOARDS_FETCHED' as const;
export type InitialBoardsFetchedPayload = {
	recentBoards: Board[];
	boardsInLocation?: BoardsPage;
};
export type InitialBoardsFetchedAction = {
	type: typeof INITIAL_BOARDS_FETCHED;
	payload: InitialBoardsFetchedPayload;
};
export const initialBoardsFetched = (
	payload: InitialBoardsFetchedPayload,
): InitialBoardsFetchedAction => ({
	type: INITIAL_BOARDS_FETCHED,
	payload,
});

export const INITIAL_BOARDS_FETCH_FAILURE = 'state.INITIAL_BOARDS_FETCH_FAILURE' as const;
export type InitialBoardsFetchFailureAction = {
	type: typeof INITIAL_BOARDS_FETCH_FAILURE;
};
export const initialBoardsFetchFailure = (): InitialBoardsFetchFailureAction => ({
	type: INITIAL_BOARDS_FETCH_FAILURE,
});

export const SET_UP_CONTEXT = 'state.SET_UP_CONTEXT' as const;
export type SetUpContextPayload = {
	baseUrl: string;
	boardsInLocation?: BoardsPage;
	currentBoard: Board | undefined;
	onBoardClick: OnBoardClick;
	currentProjectKey: string | undefined;
	onNavigateUrl: OnNavigateUrl;
};
export type SetUpContextAction = {
	type: typeof SET_UP_CONTEXT;
	payload: SetUpContextPayload;
};
export const setUpContext = (payload: SetUpContextPayload): SetUpContextAction => ({
	type: SET_UP_CONTEXT,
	payload,
});

export const ITEM_CLICKED = 'state.ITEM_CLICKED' as const;
export type ItemClickedPayload = {
	id: string;
	isViewAll: boolean;
};
export type ItemClickedAction = {
	type: typeof ITEM_CLICKED;
	payload: ItemClickedPayload;
};
export const itemClicked = (payload: ItemClickedPayload): ItemClickedAction => ({
	type: ITEM_CLICKED,
	payload,
});

export const SET_RECENTLY_CLICKED_ITEM = 'state.SET_RECENTLY_CLICKED_ITEM' as const;
export type SetRecentlyClickedItemPayload = string;
export type SetRecentlyClickedItemAction = {
	type: typeof SET_RECENTLY_CLICKED_ITEM;
	payload: SetRecentlyClickedItemPayload;
};
export const setRecentlyClickedItem = (
	payload: SetRecentlyClickedItemPayload,
): SetRecentlyClickedItemAction => ({
	type: SET_RECENTLY_CLICKED_ITEM,
	payload,
});

export const SEARCH_CHANGED = 'state.SEARCH_CHANGED' as const;
export type SearchChangedPayload = string;
export type SearchChangedAction = {
	type: typeof SEARCH_CHANGED;
	payload: SearchChangedPayload;
};
export const changeSearch = (search: string): SearchChangedAction => ({
	type: SEARCH_CHANGED,
	payload: search,
});

export const SEARCH_SUCCESS = 'state.SEARCH_SUCCESS' as const;
export type SearchSuccessPayload = {
	inLocation: BoardsPage;
	notInLocation: BoardsPage;
};
export type SearchSuccessAction = {
	type: typeof SEARCH_SUCCESS;
	payload: SearchSuccessPayload;
};
export const searchSuccess = (
	inLocation: BoardsPage,
	notInLocation: BoardsPage,
): SearchSuccessAction => ({
	type: SEARCH_SUCCESS,
	payload: { inLocation, notInLocation },
});

export const SEARCH_FAILURE = 'state.SEARCH_FAILURE' as const;
export type SearchFailureAction = {
	type: typeof SEARCH_FAILURE;
};
export const searchFailure = (): SearchFailureAction => ({
	type: SEARCH_FAILURE,
});

export const LOAD_MORE = 'state.LOAD_MORE' as const;
export type LoadMoreAction = {
	type: typeof LOAD_MORE;
};
export const loadMore = (): LoadMoreAction => ({
	type: LOAD_MORE,
});

export const LOAD_MORE_SUCCESS = 'state.LOAD_MORE_SUCCESS' as const;
export type LoadMoreSuccessPayload = BoardsPage;
export type LoadMoreSuccessAction = {
	type: typeof LOAD_MORE_SUCCESS;
	payload: LoadMoreSuccessPayload;
};
export const loadMoreSuccess = (payload: LoadMoreSuccessPayload): LoadMoreSuccessAction => ({
	type: LOAD_MORE_SUCCESS,
	payload,
});

export const LOAD_MORE_FAILURE = 'state.LOAD_MORE_FAILURE' as const;
export type LoadMoreFailureAction = {
	type: typeof LOAD_MORE_FAILURE;
};
export const loadMoreFailure = (): LoadMoreFailureAction => ({
	type: LOAD_MORE_FAILURE,
});

export const FETCHED_BOARD_OWNER_ACCOUNT_ID = 'state.FETCHED_BOARD_OWNER_ACCOUNT_ID' as const;
export type FetchedBoardOwnerAccountIdPayload = AccountId;
export type FetchedBoardOwnerAccountIdAction = {
	type: typeof FETCHED_BOARD_OWNER_ACCOUNT_ID;
	payload: FetchedBoardOwnerAccountIdPayload;
};
export const fetchedBoardOwnerAccountId = (
	payload: FetchedBoardOwnerAccountIdPayload,
): FetchedBoardOwnerAccountIdAction => ({
	type: FETCHED_BOARD_OWNER_ACCOUNT_ID,
	payload,
});
export const RETRY = 'state.RETRY' as const;
export type RetryAction = {
	type: typeof RETRY;
};
export const retry = (): RetryAction => ({
	type: RETRY,
});

export type Action =
	| FetchInitialBoardsAction
	| StartFetchingInitialBoardsAction
	| InitialBoardsFetchedAction
	| InitialBoardsFetchFailureAction
	| SetUpContextAction
	| ItemClickedAction
	| SetRecentlyClickedItemAction
	| SearchChangedAction
	| SearchSuccessAction
	| SearchFailureAction
	| LoadMoreAction
	| LoadMoreSuccessAction
	| LoadMoreFailureAction
	| FetchedBoardOwnerAccountIdAction
	| RetryAction;
